// brioCAST Partner Portal — LINE Binding Page (D5.2b)
//
// Self-service LINE binding flow for partner-portal users. Pairs with
// D5.2a API at /api/partner/line/*. Three states:
//
//   1. Unbound — show "綁定 LINE" button → fetches binding code → renders
//      QR / code / LIFF URL → polls /status for completion
//   2. Bound — show masked LINE U-id + "解除綁定" button
//   3. Loading / error — show spinner / toast
//
// API base differs from the main App api() helper: this page hits
// /api/partner/line/* (not /api/account). Helper inlined for clarity.

import { useState, useEffect, useCallback, useRef } from "react";

const t = {
  bg: "#0F1117", card: "#1A1D27", cardHover: "#22262F",
  border: "#2A2E3A", borderStrong: "#3A3F4D",
  accent: "#6C5CE7", accentLight: "#A29BFE",
  success: "#00B894", warning: "#FDCB6E", danger: "#E17055",
  text: "#E8E8ED", textMuted: "#8B8FA3", textDim: "#5A5E72",
  line: "#06C755",
  mono: "'JetBrains Mono', ui-monospace, monospace",
};

const API_PARTNER_BASE = "https://api.briolabs.io/api/partner";

async function apiPartner(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const res = await fetch(`${API_PARTNER_BASE}${path}`, {
    ...options,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `HTTP_${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

// ─── Helpers ─────────────────────────────────────────────────────────────
function formatRelTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return `${diffSec} 秒前`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} 分鐘前`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} 小時前`;
  return `${Math.floor(diffSec / 86400)} 天前`;
}

function countdown(expiresAt) {
  if (!expiresAt) return null;
  const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
  const mm = Math.floor(remaining / 60).toString().padStart(2, "0");
  const ss = (remaining % 60).toString().padStart(2, "0");
  return { remaining, label: `${mm}:${ss}` };
}

// ─── Toast ───────────────────────────────────────────────────────────────
function Toast({ kind, msg, onClose }) {
  useEffect(() => {
    const id = setTimeout(onClose, 3500);
    return () => clearTimeout(id);
  }, [onClose]);
  const bg = kind === "error" ? t.danger : kind === "success" ? t.success : t.accent;
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, padding: "12px 20px",
      background: bg, color: "#fff", borderRadius: 10, fontSize: 13,
      fontWeight: 500, zIndex: 1000,
      boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
    }}>{msg}</div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────
export default function LineBindingPage({ account }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bindingCode, setBindingCode] = useState(null);   // { code, liff_url, expires_at, expires_in_seconds }
  const [generating, setGenerating] = useState(false);
  const [unbinding, setUnbinding] = useState(false);
  const [toast, setToast] = useState(null);
  const [tick, setTick] = useState(0);
  const pollTimerRef = useRef(null);

  // D5.2d notification preferences
  const [prefs, setPrefs] = useState(null);          // { alert_levels[], paused_until, is_default }
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [draftLevels, setDraftLevels] = useState([]); // checkbox UI state, synced from prefs
  const [draftPausedUntil, setDraftPausedUntil] = useState(""); // datetime-local string

  // ── Status load ──────────────────────────────────────────────────────
  const loadStatus = useCallback(async () => {
    try {
      const data = await apiPartner("/line/status");
      // API returns { success, bound, line_user_id_masked, bound_at } at top level.
      setStatus({
        bound: !!data.bound,
        line_user_id_masked: data.line_user_id_masked || null,
        bound_at: data.bound_at || null,
      });
    } catch (e) {
      setToast({ kind: "error", msg: e.message || "無法載入綁定狀態" });
    } finally {
      setLoading(false);
    }
  }, []);

  // ── D5.2d Notification Preferences ────────────────────────────────────
  const loadPrefs = useCallback(async () => {
    setPrefsLoading(true);
    try {
      const data = await apiPartner("/line/notification-preferences");
      if (data && data.success) {
        setPrefs(data);
        setDraftLevels(Array.isArray(data.alert_levels) ? data.alert_levels : []);
        setDraftPausedUntil(data.paused_until ? toLocalDatetimeInput(data.paused_until) : "");
      }
    } catch (e) {
      console.error("[prefs] load failed", e);
      setToast({ kind: "error", msg: "讀取通知設定失敗" });
    } finally {
      setPrefsLoading(false);
    }
  }, []);

  const savePrefs = useCallback(async () => {
    setSavingPrefs(true);
    try {
      const body = { alert_levels: draftLevels };
      if (draftPausedUntil) {
        body.paused_until = new Date(draftPausedUntil).toISOString();
      } else {
        body.paused_until = null;
      }
      const data = await apiPartner("/line/notification-preferences", {
        method: "PUT",
        body,
      });
      if (data && data.success) {
        setPrefs(data);
        setDraftLevels(Array.isArray(data.alert_levels) ? data.alert_levels : []);
        setDraftPausedUntil(data.paused_until ? toLocalDatetimeInput(data.paused_until) : "");
        setToast({ kind: "success", msg: "通知設定已儲存" });
      }
    } catch (e) {
      const msg = (e && e.body && e.body.error) || (e && e.message) || "儲存失敗";
      setToast({ kind: "error", msg: `儲存通知設定失敗：${msg}` });
    } finally {
      setSavingPrefs(false);
    }
  }, [draftLevels, draftPausedUntil]);

  const resetPrefs = useCallback(async () => {
    if (!window.confirm("確定要回復預設通知設定嗎？")) return;
    setSavingPrefs(true);
    try {
      const data = await apiPartner("/line/notification-preferences", { method: "DELETE" });
      if (data && data.success) {
        setPrefs(data);
        setDraftLevels(Array.isArray(data.alert_levels) ? data.alert_levels : []);
        setDraftPausedUntil("");
        setToast({ kind: "success", msg: "已回復預設通知設定" });
      }
    } catch (e) {
      setToast({ kind: "error", msg: "回復預設失敗" });
    } finally {
      setSavingPrefs(false);
    }
  }, []);

  function toLocalDatetimeInput(iso) {
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ""; }
  }

  function toggleLevel(level) {
    setDraftLevels((cur) =>
      cur.includes(level) ? cur.filter((l) => l !== level) : [...cur, level]
    );
  }

  // Load prefs when LINE binding becomes active
  useEffect(() => {
    if (status?.bound) loadPrefs();
    else { setPrefs(null); setDraftLevels([]); setDraftPausedUntil(""); }
  }, [status?.bound, loadPrefs]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // ── Tick once per second so countdown re-renders ─────────────────────
  useEffect(() => {
    if (!bindingCode) return;
    const id = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [bindingCode]);

  // ── Auto-expire the displayed code when it passes expires_at ────────
  useEffect(() => {
    if (!bindingCode) return;
    const c = countdown(bindingCode.expires_at);
    if (c && c.remaining <= 0) {
      setBindingCode(null);
      setToast({ kind: "error", msg: "綁定代碼已過期，請重新產生" });
    }
  }, [tick, bindingCode]);

  // ── Poll status every 3s while a code is active so we detect when LIFF completes ──
  useEffect(() => {
    if (!bindingCode) {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }
    pollTimerRef.current = setInterval(async () => {
      try {
        const data = await apiPartner("/line/status");
        if (data.bound) {
          setStatus({
            bound: true,
            line_user_id_masked: data.line_user_id_masked,
            bound_at: data.bound_at,
          });
          setBindingCode(null);
          setToast({ kind: "success", msg: "✅ LINE 帳號綁定成功" });
        }
      } catch (_) { /* ignore poll errors */ }
    }, 3000);
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [bindingCode]);

  // ── Actions ──────────────────────────────────────────────────────────
  const generateCode = async () => {
    setGenerating(true);
    try {
      const data = await apiPartner("/line/binding-code", { method: "POST" });
      setBindingCode({
        code: data.code,
        liff_url: data.liff_url,
        expires_at: data.expires_at,
        expires_in_seconds: data.expires_in_seconds,
      });
      setToast({ kind: "info", msg: "綁定代碼已產生，請使用 LINE 完成綁定" });
    } catch (e) {
      setToast({ kind: "error", msg: e.message || "產生綁定代碼失敗" });
    } finally {
      setGenerating(false);
    }
  };

  const cancelBinding = () => {
    setBindingCode(null);
  };

  const unbind = async () => {
    if (!window.confirm("確定要解除 LINE 綁定嗎？解除後將不再收到設備告警通知。")) return;
    setUnbinding(true);
    try {
      await apiPartner("/line/binding", { method: "DELETE" });
      setStatus({ bound: false, line_user_id_masked: null, bound_at: null });
      setToast({ kind: "success", msg: "已解除 LINE 綁定" });
    } catch (e) {
      setToast({ kind: "error", msg: e.message || "解除綁定失敗" });
    } finally {
      setUnbinding(false);
    }
  };

  const copyCode = () => {
    if (!bindingCode) return;
    navigator.clipboard.writeText(bindingCode.code).then(
      () => setToast({ kind: "success", msg: "已複製代碼" }),
      () => setToast({ kind: "error", msg: "複製失敗" })
    );
  };

  // ── Render ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 40, color: t.textMuted }}>載入中…</div>
    );
  }

  const cd = bindingCode ? countdown(bindingCode.expires_at) : null;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: t.text, margin: 0 }}>
            LINE 通知綁定
          </h1>
          <span style={{
            padding: "4px 10px", background: t.line, color: "#fff",
            fontSize: 11, fontWeight: 600, borderRadius: 6,
          }}>D5.2b</span>
        </div>
        <div style={{ fontSize: 13, color: t.textMuted }}>
          綁定 LINE 帳號後，當設備發生嚴重異常、被撤銷或長時間無回應時，將即時推播通知至您的 LINE。
        </div>
      </div>

      {/* ── Bound state ───────────────────────────────────────────── */}
      {status?.bound && !bindingCode && (
        <div style={{
          background: t.card, border: `1px solid ${t.border}`, borderRadius: 12,
          padding: 24,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12, background: t.line,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 24,
            }}>💬</div>
            <div>
              <div style={{ fontSize: 14, color: t.textMuted, marginBottom: 4 }}>目前已綁定</div>
              <div style={{ fontSize: 16, color: t.text, fontFamily: t.mono, fontWeight: 600 }}>
                {status.line_user_id_masked || "—"}
              </div>
              <div style={{ fontSize: 11, color: t.textDim, marginTop: 4 }}>
                綁定時間：{formatRelTime(status.bound_at)}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={unbind}
              disabled={unbinding}
              style={{
                padding: "10px 18px", background: "transparent",
                color: t.danger, border: `1px solid ${t.danger}`,
                borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: unbinding ? "wait" : "pointer", opacity: unbinding ? 0.5 : 1,
              }}
            >{unbinding ? "解除中…" : "解除綁定"}</button>
            <button
              onClick={generateCode}
              disabled={generating}
              style={{
                padding: "10px 18px", background: "transparent",
                color: t.accent, border: `1px solid ${t.accent}`,
                borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: generating ? "wait" : "pointer", opacity: generating ? 0.5 : 1,
              }}
            >{generating ? "產生中…" : "更換綁定 LINE"}</button>
          </div>
        </div>
      )}

      {/* ── D5.2d Notification Preferences (only when bound) ──── */}
      {status?.bound && !bindingCode && (
        <div style={{
          background: t.card, border: `1px solid ${t.border}`, borderRadius: 12,
          padding: 24, marginTop: 16,
        }}>
          <div style={{ fontSize: 16, color: t.text, fontWeight: 600, marginBottom: 6 }}>
            通知設定
          </div>
          <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 20 }}>
            選擇要接收哪些等級的設備警示通知。取消全部勾選即為「全部關閉」。
          </div>

          {prefsLoading && (
            <div style={{ color: t.textMuted, fontSize: 13 }}>讀取中…</div>
          )}

          {!prefsLoading && prefs && (
            <>
              {/* Alert level checkboxes */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
                {[
                  { level: "critical",  label: "嚴重 (Critical)",  hint: "立即影響服務的故障" },
                  { level: "unhealthy", label: "不健康 (Unhealthy)", hint: "設備異常但仍可運作" },
                  { level: "warning",   label: "警告 (Warning)",    hint: "效能下降、需注意" },
                  { level: "revoked",   label: "撤銷 (Revoked)",    hint: "設備被撤銷或停用" },
                  { level: "stale",     label: "失聯 (Stale)",      hint: "長時間無心跳回報" },
                ].map(({ level, label, hint }) => (
                  <label
                    key={level}
                    style={{
                      display: "flex", alignItems: "center", gap: 12,
                      padding: "10px 12px",
                      background: draftLevels.includes(level) ? t.cardHover : "transparent",
                      border: `1px solid ${draftLevels.includes(level) ? t.line : t.border}`,
                      borderRadius: 8, cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={draftLevels.includes(level)}
                      onChange={() => toggleLevel(level)}
                      style={{ accentColor: t.line, width: 16, height: 16, cursor: "pointer" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, color: t.text, fontWeight: 500 }}>{label}</div>
                      <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>{hint}</div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Paused-until */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, color: t.text, fontWeight: 500, marginBottom: 6 }}>
                  暫停通知到（可選）
                </div>
                <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 8 }}>
                  設定後，無論勾選哪些等級，在指定時間點前都不會發送通知。
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="datetime-local"
                    value={draftPausedUntil}
                    onChange={(e) => setDraftPausedUntil(e.target.value)}
                    style={{
                      padding: "8px 10px", background: t.bg, border: `1px solid ${t.borderStrong}`,
                      borderRadius: 6, color: t.text, fontSize: 13, fontFamily: t.mono,
                    }}
                  />
                  {draftPausedUntil && (
                    <button
                      onClick={() => setDraftPausedUntil("")}
                      style={{
                        padding: "6px 12px", background: "transparent",
                        color: t.textMuted, border: `1px solid ${t.border}`,
                        borderRadius: 6, fontSize: 12, cursor: "pointer",
                      }}
                    >清除</button>
                  )}
                </div>
              </div>

              {/* Save + reset */}
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button
                  onClick={savePrefs}
                  disabled={savingPrefs}
                  style={{
                    padding: "10px 20px", background: t.line, color: "#fff",
                    border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600,
                    cursor: savingPrefs ? "wait" : "pointer", opacity: savingPrefs ? 0.5 : 1,
                  }}
                >{savingPrefs ? "儲存中…" : "儲存設定"}</button>
                <button
                  onClick={resetPrefs}
                  disabled={savingPrefs}
                  style={{
                    padding: "10px 16px", background: "transparent",
                    color: t.textMuted, border: `1px solid ${t.border}`,
                    borderRadius: 8, fontSize: 13, cursor: savingPrefs ? "wait" : "pointer",
                  }}
                >回復預設</button>
                {prefs.is_default && (
                  <span style={{ fontSize: 12, color: t.textDim, marginLeft: 4 }}>
                    （目前為預設值）
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Unbound state, no code yet ──────────────────────────── */}
      {!status?.bound && !bindingCode && (
        <div style={{
          background: t.card, border: `1px solid ${t.border}`, borderRadius: 12,
          padding: 32, textAlign: "center",
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>💬</div>
          <div style={{ fontSize: 16, color: t.text, fontWeight: 600, marginBottom: 8 }}>
            尚未綁定 LINE 帳號
          </div>
          <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 24, maxWidth: 480, margin: "0 auto 24px" }}>
            點擊下方按鈕產生一組 10 分鐘有效的綁定代碼，並使用 LINE 完成綁定流程。
          </div>
          <button
            onClick={generateCode}
            disabled={generating}
            style={{
              padding: "14px 32px", background: t.line, color: "#fff",
              border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700,
              cursor: generating ? "wait" : "pointer", opacity: generating ? 0.6 : 1,
            }}
          >{generating ? "產生中…" : "🔗 產生綁定代碼"}</button>
        </div>
      )}

      {/* ── Code displayed, waiting for LIFF completion ─────────── */}
      {bindingCode && (
        <div style={{
          background: t.card, border: `1px solid ${t.border}`, borderRadius: 12,
          padding: 32,
        }}>
          <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 12 }}>
            請使用 LINE 開啟下方連結，或在 brioCAST OA 內輸入此代碼以完成綁定。
            完成綁定後本頁面會自動更新。
          </div>

          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 12, padding: "24px 16px", background: "#0B0D14",
            border: `2px solid ${t.line}`, borderRadius: 10, marginBottom: 16,
          }}>
            <code style={{
              fontFamily: t.mono, fontSize: 32, fontWeight: 700,
              color: t.line, letterSpacing: 6,
            }}>{bindingCode.code}</code>
            <button
              onClick={copyCode}
              title="複製代碼"
              style={{
                padding: "8px 14px", background: t.accent, color: "#fff",
                border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600,
                cursor: "pointer",
              }}
            >複製</button>
          </div>

          {/* Countdown */}
          <div style={{
            fontSize: 13, color: cd && cd.remaining < 60 ? t.warning : t.textMuted,
            textAlign: "center", marginBottom: 16, fontFamily: t.mono,
          }}>
            {cd ? `代碼有效時間 ${cd.label}` : "已過期"}
          </div>

          {bindingCode.liff_url ? (
            <a
              href={bindingCode.liff_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "block", padding: "12px 24px",
                background: t.line, color: "#fff", textAlign: "center",
                textDecoration: "none", borderRadius: 10, fontSize: 14,
                fontWeight: 700, marginBottom: 12,
              }}
            >📱 在 LINE 中開啟綁定頁面</a>
          ) : (
            <div style={{
              padding: "10px 16px", background: "#22262F", borderRadius: 8,
              fontSize: 12, color: t.textMuted, marginBottom: 12,
              textAlign: "center",
            }}>
              LIFF 應用尚未設定（LIFF_DEVICEOPS_BINDING_URL 環境變數）。
              請在 brioCAST LINE OA 中輸入上方代碼以完成綁定。
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              onClick={cancelBinding}
              style={{
                padding: "8px 16px", background: "transparent",
                color: t.textMuted, border: `1px solid ${t.border}`,
                borderRadius: 8, fontSize: 12, fontWeight: 500,
                cursor: "pointer",
              }}
            >取消</button>
          </div>
        </div>
      )}

      {/* ── Footer help ─────────────────────────────────────────── */}
      <div style={{
        marginTop: 20, padding: 16, background: "#0B0D14",
        border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 12,
        color: t.textDim, lineHeight: 1.6,
      }}>
        <div style={{ color: t.textMuted, fontWeight: 600, marginBottom: 6 }}>
          綁定後您將收到以下事件的通知：
        </div>
        🚨 設備嚴重異常（critical）<br />
        ⚠️ 設備異常或長時間離線（unhealthy / stale）<br />
        🛑 設備被撤銷（device.revoked）
      </div>

      {toast && <Toast kind={toast.kind} msg={toast.msg} onClose={() => setToast(null)} />}
    </div>
  );
}
